import type { MiceProjectView } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Card, Dot, ToneChip } from '@web/ui/core';

interface EventSetupChecklistProps {
  project: MiceProjectView;
  travelers: number;
  canManage: boolean;
  onPremises: () => void;
  onImport: () => void;
}

/** O checklist aparece para quem gerencia, até as premissas e os viajantes estarem feitos. */
export function setupPending(
  project: MiceProjectView,
  travelers: number,
  canManage: boolean,
): boolean {
  return canManage && (project.premisesCheckedAt === null || travelers === 0);
}

/** Depois de criar o evento: conferir as premissas e importar os viajantes, até os dois estarem feitos. */
export function EventSetupChecklist(props: Readonly<EventSetupChecklistProps>) {
  const premisesDone = props.project.premisesCheckedAt !== null;
  const travelersDone = props.travelers > 0;
  if (!setupPending(props.project, props.travelers, props.canManage)) return null;
  const steps = [
    {
      key: 'premises' as const,
      done: premisesDone,
      label: t('mice.premises.check'),
      run: props.onPremises,
    },
    {
      key: 'travelers' as const,
      done: travelersDone,
      label: t('mice.import.open'),
      run: props.onImport,
    },
  ];
  return (
    <Card>
      <section aria-label={t('mice.setup.title')} className="flex flex-col gap-3 p-4">
        <span className="text-meta font-medium text-foreground">{t('mice.setup.title')}</span>
        <ol className="flex flex-col gap-2">
          {steps.map((step, i) => (
            <li key={step.key} className="flex items-center gap-3">
              <Dot tone={step.done ? 'green' : 'gray'} size="md" />
              <span className="flex-1 text-meta text-foreground-default">
                {t(`mice.setup.${step.key}`)}
              </span>
              {step.done ? (
                <ToneChip tone="green">{t('mice.setup.done')}</ToneChip>
              ) : (
                <Button size="sm" variant={i === 0 ? 'primary' : 'outline'} onClick={step.run}>
                  {step.label}
                </Button>
              )}
            </li>
          ))}
        </ol>
      </section>
    </Card>
  );
}
